// ClickUp Dashboard — port React/tRPC do app standalone.
// Visível só pra raz admin (rota razAdminOnly + razAdminProcedure no server).
//
// Mostra:
//   - Dobra "Para hoje" (tasks com due_date hoje + atrasadas em aberto)
//   - Tabela principal com filtros multi-select (cliente, área, status, equipe)
//   - Sort por coluna, busca por projeto
//   - Botão Re-sincronizar (puxa tudo da API ClickUp)

import { useState, useMemo, useEffect, useRef } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { RefreshCw, ExternalLink, X, Search } from "lucide-react";

// ─── Helpers ──────────────────────────────────────────────────────────────────

interface Task {
  id: string;
  name: string;
  status: string | null;
  status_type: string | null;
  status_color: string | null;
  space_id: string | null;
  space_name: string | null;
  folder_id: string | null;
  folder_name: string | null;
  list_id: string | null;
  list_name: string | null;
  priority: string | null;
  due_date: number | null;
  start_date: number | null;
  assignees: string;
  url: string;
  date_updated: number | null;
  synced_at: number | null;
}

interface Assignee {
  id: number;
  username: string;
  initials?: string;
  color?: string;
}

const isDone = (t: Task) => t.status_type === "closed";
const startOfToday = () => { const d = new Date(); d.setHours(0,0,0,0); return d.getTime(); };
const endOfToday   = () => { const d = new Date(); d.setHours(23,59,59,999); return d.getTime(); };
const addDays      = (ms: number, n: number) => ms + n * 86_400_000;

function parseAssignees(json: string): Assignee[] {
  try { return JSON.parse(json || "[]"); } catch { return []; }
}

// ─── Cor por cliente (hash estável do nome → paleta) ─────────────────────────
const CLIENT_PALETTE = [
  { bg: "#FEE2E2", fg: "#991B1B" }, // vermelho
  { bg: "#FED7AA", fg: "#9A3412" }, // laranja
  { bg: "#FEF3C7", fg: "#854D0E" }, // âmbar
  { bg: "#D1FAE5", fg: "#065F46" }, // verde
  { bg: "#CFFAFE", fg: "#155E75" }, // ciano
  { bg: "#DBEAFE", fg: "#1E40AF" }, // azul
  { bg: "#E0E7FF", fg: "#3730A3" }, // indigo
  { bg: "#EDE9FE", fg: "#5B21B6" }, // violeta
  { bg: "#FAE8FF", fg: "#86198F" }, // fúcsia
  { bg: "#FCE7F3", fg: "#9D174D" }, // rosa
  { bg: "#F3E8FF", fg: "#6B21A8" }, // roxo
  { bg: "#E7E5E4", fg: "#1C1917" }, // stone
];
function clientColor(name: string | null | undefined): { bg: string; fg: string } {
  if (!name) return { bg: "#F3F4F6", fg: "#4B5563" };
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return CLIENT_PALETTE[Math.abs(h) % CLIENT_PALETTE.length];
}

function ClientPill({ name }: { name: string | null | undefined }) {
  if (!name) return <span className="text-muted-foreground">—</span>;
  const { bg, fg } = clientColor(name);
  return (
    <span
      className="inline-block text-[11px] font-semibold px-2 py-0.5 rounded-md whitespace-nowrap"
      style={{ background: bg, color: fg }}
    >
      {name}
    </span>
  );
}

// ─── Filtro de data: presets + período custom ───────────────────────────────
type DateFilter = "todas" | "atrasadas" | "hoje" | "amanha" | "esta_semana" | "prox7" | "prox30" | "sem_data";

const DATE_FILTER_OPTS: { value: DateFilter; label: string }[] = [
  { value: "todas",        label: "Todas datas" },
  { value: "atrasadas",    label: "Atrasadas (em aberto)" },
  { value: "hoje",          label: "Hoje" },
  { value: "amanha",        label: "Amanhã" },
  { value: "esta_semana",   label: "Esta semana" },
  { value: "prox7",         label: "Próx. 7 dias" },
  { value: "prox30",        label: "Próx. 30 dias" },
  { value: "sem_data",      label: "Sem data" },
];

function applyDateFilter(t: Task, filter: DateFilter): boolean {
  if (filter === "todas") return true;
  if (filter === "sem_data") return t.due_date == null;
  if (t.due_date == null) return false;
  const now = startOfToday();
  const today = endOfToday();
  const due = Number(t.due_date);

  switch (filter) {
    case "atrasadas":   return due < now && !isDone(t);
    case "hoje":         return due >= now && due <= today;
    case "amanha":       return due >= addDays(now, 1) && due <= addDays(today, 1);
    case "esta_semana": {
      // segunda → domingo da semana atual (PT-BR: 1=seg ... 0=dom)
      const d = new Date(now);
      const dow = d.getDay(); // 0=dom, 1=seg, ...
      const daysToMonday = (dow + 6) % 7;
      const monday = addDays(now, -daysToMonday);
      const sunday = addDays(monday, 7) - 1;
      return due >= monday && due <= sunday;
    }
    case "prox7":         return due >= now && due <= addDays(today, 7);
    case "prox30":        return due >= now && due <= addDays(today, 30);
    default: return true;
  }
}

function fmtDate(ms: number | null): { text: string; cls: string } {
  if (!ms) return { text: "—", cls: "" };
  const d = new Date(Number(ms));
  const today = new Date(); today.setHours(0,0,0,0);
  const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
  const text = d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
  let cls = "";
  if (diff < 0) cls = "text-red-600 font-semibold";
  else if (diff <= 2) cls = "text-amber-600 font-medium";
  const suffix =
    diff < 0 ? ` (${-diff}d atrás)` :
    diff === 0 ? " (hoje)" :
    diff <= 7 ? ` (${diff}d)` : "";
  return { text: text + suffix, cls };
}

// ─── Filtro multi-select com checkbox ────────────────────────────────────────

function MultiSelect({ options, selected, onChange, placeholder }: {
  options: string[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  const filtered = options.filter(o => o.toLowerCase().includes(search.toLowerCase()));
  const label =
    selected.size === 0 ? (placeholder ?? "Todos") :
    selected.size === 1 ? Array.from(selected)[0] :
    `${selected.size} selecionados`;
  const active = selected.size > 0;

  function toggle(opt: string) {
    const next = new Set(selected);
    if (next.has(opt)) next.delete(opt); else next.add(opt);
    onChange(next);
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        className={`w-full text-left text-xs px-2.5 py-1.5 rounded-md border bg-background ${
          active ? "border-foreground font-medium" : "border-border text-muted-foreground"
        }`}
      >
        {label} ▾
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 w-56 max-h-72 bg-card border border-border rounded-lg shadow-lg z-20 flex flex-col">
          <div className="p-2 border-b border-border">
            <Input
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar…"
              className="text-xs h-7"
            />
          </div>
          {selected.size > 0 && (
            <button
              onClick={() => onChange(new Set())}
              className="text-[11px] text-muted-foreground hover:text-foreground px-3 py-1.5 text-left border-b border-border"
            >
              Limpar seleção ({selected.size})
            </button>
          )}
          <div className="flex-1 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <p className="text-[11px] text-muted-foreground px-3 py-2 italic">nada encontrado</p>
            ) : filtered.map(opt => (
              <label key={opt} className="flex items-center gap-2 px-3 py-1.5 hover:bg-accent cursor-pointer text-xs">
                <input
                  type="checkbox"
                  checked={selected.has(opt)}
                  onChange={() => toggle(opt)}
                  className="w-3.5 h-3.5 accent-foreground"
                />
                <span className="truncate">{opt}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Pill de status ──────────────────────────────────────────────────────────

function StatusPill({ task }: { task: Task }) {
  const c = task.status_color || "#8b90a0";
  return (
    <span
      className="inline-block text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap"
      style={{ background: `${c}22`, color: c, border: `1px solid ${c}55` }}
    >
      {task.status || "—"}
    </span>
  );
}

function TeamCell({ json }: { json: string }) {
  const list = parseAssignees(json);
  if (!list.length) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {list.map((a, i) => {
        const c = a.color || "#5f55ee";
        return (
          <span
            key={i}
            className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-full"
            style={{ border: `1px solid ${c}55` }}
            title={a.username}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: c }} />
            {a.username}
          </span>
        );
      })}
    </div>
  );
}

// ─── Página ───────────────────────────────────────────────────────────────────

type SortKey = "folder_name" | "name" | "due_date" | "list_name" | "status";

export default function ClickUpDashboard() {
  const utils = trpc.useUtils();
  const tasksQuery = trpc.clickup.list.useQuery(undefined, {
    refetchInterval: 30_000,
    refetchOnWindowFocus: false,
  });
  const syncMut = trpc.clickup.syncNow.useMutation({
    onSuccess: (r) => {
      toast.success(`Sync OK — ${r.spaces} spaces, ${r.tasks} tarefas em ${r.elapsedMs}ms`);
      utils.clickup.list.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });
  const statusQuery = trpc.clickup.status.useQuery();

  const tasks = (tasksQuery.data ?? []) as Task[];

  // Filtros tabela principal
  const [fCliente, setFCliente]   = useState<Set<string>>(new Set());
  const [fArea, setFArea]         = useState<Set<string>>(new Set());
  const [fStatus, setFStatus]     = useState<Set<string>>(new Set());
  const [fEquipe, setFEquipe]     = useState<Set<string>>(new Set());
  const [fProjeto, setFProjeto]   = useState("");
  const [fData, setFData]         = useState<DateFilter>("todas");
  const [showDone, setShowDone]   = useState(false);
  const [sortKey, setSortKey]     = useState<SortKey>("due_date");
  const [sortAsc, setSortAsc]     = useState(true);

  // Filtros "Para hoje" (independentes)
  const [tCliente, setTCliente]   = useState<Set<string>>(new Set());
  const [tArea, setTArea]         = useState<Set<string>>(new Set());
  const [tStatus, setTStatus]     = useState<Set<string>>(new Set());
  const [tEquipe, setTEquipe]     = useState<Set<string>>(new Set());
  const [tProjeto, setTProjeto]   = useState("");
  const [todayOpen, setTodayOpen] = useState(true);

  // Opções derivadas das tasks
  const { clientes, areas, statuses, pessoas } = useMemo(() => {
    const cli = new Set<string>(), are = new Set<string>(), sts = new Set<string>(), pes = new Set<string>();
    for (const t of tasks) {
      if (t.folder_name) cli.add(t.folder_name);
      if (t.list_name)   are.add(t.list_name);
      if (t.status)      sts.add(t.status);
      for (const a of parseAssignees(t.assignees)) if (a.username) pes.add(a.username);
    }
    const sort = (s: Set<string>) => Array.from(s).sort();
    return { clientes: sort(cli), areas: sort(are), statuses: sort(sts), pessoas: sort(pes) };
  }, [tasks]);

  // Linhas "Para hoje"
  const todayRows = useMemo(() => {
    const s = startOfToday(), e = endOfToday();
    return tasks
      .filter(t => {
        if (!(t.due_date && t.due_date >= s && t.due_date <= e && !isDone(t))) return false;
        if (tCliente.size && !tCliente.has(t.folder_name ?? "")) return false;
        if (tArea.size    && !tArea.has(t.list_name ?? ""))     return false;
        if (tStatus.size  && !tStatus.has(t.status ?? ""))      return false;
        if (tEquipe.size) {
          const names = parseAssignees(t.assignees).map(a => a.username);
          if (!names.some(n => tEquipe.has(n))) return false;
        }
        if (tProjeto && !(t.name ?? "").toLowerCase().includes(tProjeto.toLowerCase())) return false;
        return true;
      })
      .sort((a, b) => (a.due_date ?? 0) - (b.due_date ?? 0));
  }, [tasks, tCliente, tArea, tStatus, tEquipe, tProjeto]);

  // Linhas tabela principal
  const mainRows = useMemo(() => {
    let rows = tasks.filter(t => {
      if (!applyDateFilter(t, fData)) return false;
      if (fCliente.size && !fCliente.has(t.folder_name ?? "")) return false;
      if (fArea.size    && !fArea.has(t.list_name ?? "")) return false;
      if (fStatus.size) {
        if (!fStatus.has(t.status ?? "")) return false;
      } else if (!showDone && isDone(t)) {
        return false;
      }
      if (fEquipe.size) {
        const names = parseAssignees(t.assignees).map(a => a.username);
        if (!names.some(n => fEquipe.has(n))) return false;
      }
      if (fProjeto && !(t.name ?? "").toLowerCase().includes(fProjeto.toLowerCase())) return false;
      return true;
    });
    rows.sort((a, b) => {
      let x: any = (a as any)[sortKey], y: any = (b as any)[sortKey];
      if (sortKey === "due_date") { x = x ?? Infinity; y = y ?? Infinity; }
      x = x ?? ""; y = y ?? "";
      if (x < y) return sortAsc ? -1 : 1;
      if (x > y) return sortAsc ? 1 : -1;
      return 0;
    });
    return rows;
  }, [tasks, fCliente, fArea, fStatus, fEquipe, fProjeto, fData, showDone, sortKey, sortAsc]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(true); }
  }

  const cfgOk = statusQuery.data?.configured;

  return (
    <div className="p-4 sm:p-6 space-y-5 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            ClickUp <span className="text-muted-foreground font-normal">— Painel centralizado</span>
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {mainRows.length} de {tasks.length} tarefas
            {fData !== "todas" && (
              <span className="ml-2 text-foreground">· filtro: {DATE_FILTER_OPTS.find(o => o.value === fData)?.label}</span>
            )}
            {!cfgOk && <span className="text-red-600 ml-2">⚠ CLICKUP_TOKEN ou TEAM_ID não configurado no servidor</span>}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Filtro de data — afeta a tabela principal (a dobra 'Para hoje' tem recorte próprio) */}
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Data</span>
            <select
              value={fData}
              onChange={e => setFData(e.target.value as DateFilter)}
              className={`text-xs rounded-md border px-2 py-1.5 bg-background ${
                fData !== "todas" ? "border-foreground font-semibold" : "border-border"
              }`}
            >
              {DATE_FILTER_OPTS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <label className="text-xs flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={showDone}
              onChange={e => setShowDone(e.target.checked)}
              className="w-3.5 h-3.5 accent-foreground"
            />
            Mostrar concluídas (done)
          </label>
          <Button
            size="sm"
            onClick={() => syncMut.mutate()}
            disabled={syncMut.isPending || !cfgOk}
            className="gap-1.5"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${syncMut.isPending ? "animate-spin" : ""}`} />
            {syncMut.isPending ? "Sincronizando…" : "Re-sincronizar"}
          </Button>
        </div>
      </div>

      {/* Dobra "Para hoje" */}
      <section className="rounded-xl border border-border bg-card overflow-hidden">
        <button
          onClick={() => setTodayOpen(o => !o)}
          className="w-full px-4 py-3 flex items-center gap-2 bg-[#0A0A0A] text-white"
        >
          <span className="text-sm">{todayOpen ? "▾" : "▸"}</span>
          <span className="font-semibold text-sm">Para hoje</span>
          <span className="text-xs bg-white/10 px-2 py-0.5 rounded-full">{todayRows.length}</span>
        </button>
        {todayOpen && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/50">
                <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  <th className="text-left px-3 py-2 w-[15%]">Cliente</th>
                  <th className="text-left px-3 py-2 w-[30%]">Projeto</th>
                  <th className="text-left px-3 py-2 w-[8%]">Click Up</th>
                  <th className="text-left px-3 py-2 w-[12%]">Data de entrega</th>
                  <th className="text-left px-3 py-2 w-[10%]">Área</th>
                  <th className="text-left px-3 py-2 w-[10%]">Status</th>
                  <th className="text-left px-3 py-2 w-[15%]">Equipe</th>
                </tr>
                <tr className="bg-card border-y border-border">
                  <th className="px-2 py-1.5"><MultiSelect options={clientes} selected={tCliente} onChange={setTCliente} /></th>
                  <th className="px-2 py-1.5">
                    <Input value={tProjeto} onChange={e => setTProjeto(e.target.value)}
                      placeholder="Filtrar projeto..." className="h-7 text-xs" />
                  </th>
                  <th></th>
                  <th></th>
                  <th className="px-2 py-1.5"><MultiSelect options={areas} selected={tArea} onChange={setTArea} /></th>
                  <th className="px-2 py-1.5"><MultiSelect options={statuses} selected={tStatus} onChange={setTStatus} /></th>
                  <th className="px-2 py-1.5"><MultiSelect options={pessoas} selected={tEquipe} onChange={setTEquipe} /></th>
                </tr>
              </thead>
              <tbody>
                {todayRows.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-6 text-muted-foreground italic">Nada para hoje. </td></tr>
                ) : todayRows.map(t => {
                  const due = fmtDate(t.due_date);
                  return (
                    <tr key={t.id} className="border-t border-border hover:bg-accent">
                      <td className="px-3 py-2"><ClientPill name={t.folder_name} /></td>
                      <td className="px-3 py-2">{t.name}</td>
                      <td className="px-3 py-2">
                        {t.url ? (
                          <a href={t.url} target="_blank" rel="noopener noreferrer"
                            className="text-blue-600 hover:underline inline-flex items-center gap-0.5">
                            Abrir <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : "—"}
                      </td>
                      <td className={`px-3 py-2 ${due.cls}`}>{due.text}</td>
                      <td className="px-3 py-2">{t.list_name ?? "—"}</td>
                      <td className="px-3 py-2"><StatusPill task={t} /></td>
                      <td className="px-3 py-2"><TeamCell json={t.assignees} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Tabela principal */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/50">
              <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="text-left px-3 py-2 cursor-pointer hover:text-foreground w-[15%]" onClick={() => toggleSort("folder_name")}>Cliente {sortKey === "folder_name" ? (sortAsc ? "▲" : "▼") : ""}</th>
                <th className="text-left px-3 py-2 cursor-pointer hover:text-foreground w-[30%]" onClick={() => toggleSort("name")}>Projeto {sortKey === "name" ? (sortAsc ? "▲" : "▼") : ""}</th>
                <th className="text-left px-3 py-2 w-[8%]">Click Up</th>
                <th className="text-left px-3 py-2 cursor-pointer hover:text-foreground w-[12%]" onClick={() => toggleSort("due_date")}>Data de entrega {sortKey === "due_date" ? (sortAsc ? "▲" : "▼") : ""}</th>
                <th className="text-left px-3 py-2 cursor-pointer hover:text-foreground w-[10%]" onClick={() => toggleSort("list_name")}>Área {sortKey === "list_name" ? (sortAsc ? "▲" : "▼") : ""}</th>
                <th className="text-left px-3 py-2 cursor-pointer hover:text-foreground w-[10%]" onClick={() => toggleSort("status")}>Status {sortKey === "status" ? (sortAsc ? "▲" : "▼") : ""}</th>
                <th className="text-left px-3 py-2 w-[15%]">Equipe</th>
              </tr>
              <tr className="bg-card border-y border-border">
                <th className="px-2 py-1.5"><MultiSelect options={clientes} selected={fCliente} onChange={setFCliente} /></th>
                <th className="px-2 py-1.5">
                  <Input value={fProjeto} onChange={e => setFProjeto(e.target.value)}
                    placeholder="Filtrar projeto..." className="h-7 text-xs" />
                </th>
                <th></th>
                <th></th>
                <th className="px-2 py-1.5"><MultiSelect options={areas} selected={fArea} onChange={setFArea} /></th>
                <th className="px-2 py-1.5"><MultiSelect options={statuses} selected={fStatus} onChange={setFStatus} /></th>
                <th className="px-2 py-1.5"><MultiSelect options={pessoas} selected={fEquipe} onChange={setFEquipe} /></th>
              </tr>
            </thead>
            <tbody>
              {tasksQuery.isLoading ? (
                <tr><td colSpan={7} className="text-center py-10 text-muted-foreground">Carregando tarefas…</td></tr>
              ) : mainRows.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-10 text-muted-foreground italic">
                  {tasks.length === 0 ? (
                    "Nenhuma tarefa. Rode o sync inicial."
                  ) : fData !== "todas" ? (
                    <>Nenhuma tarefa com filtro de data <strong>{DATE_FILTER_OPTS.find(o => o.value === fData)?.label}</strong>.</>
                  ) : (
                    "Nenhuma tarefa bate com os filtros."
                  )}
                </td></tr>
              ) : mainRows.map(t => {
                const due = fmtDate(t.due_date);
                return (
                  <tr key={t.id} className="border-t border-border hover:bg-accent">
                    <td className="px-3 py-2">{t.folder_name ?? "—"}</td>
                    <td className="px-3 py-2">{t.name}</td>
                    <td className="px-3 py-2">
                      {t.url ? (
                        <a href={t.url} target="_blank" rel="noopener noreferrer"
                          className="text-blue-600 hover:underline inline-flex items-center gap-0.5">
                          Abrir <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : "—"}
                    </td>
                    <td className={`px-3 py-2 ${due.cls}`}>{due.text}</td>
                    <td className="px-3 py-2">{t.list_name ?? "—"}</td>
                    <td className="px-3 py-2"><StatusPill task={t} /></td>
                    <td className="px-3 py-2"><TeamCell json={t.assignees} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
